import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Package, CheckCircle, Send, Trash2, Split, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';

const BundleSplit = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  const [originalBundle, setOriginalBundle] = useState('');
  const [originalBundleData, setOriginalBundleData] = useState(null);
  const [originalBundleLocked, setOriginalBundleLocked] = useState(false);
  const [isValidatingOriginal, setIsValidatingOriginal] = useState(false);
  const [newBundleCode, setNewBundleCode] = useState('');
  // Each entry: {bundle_barcode, sort_no, color_code, color_name}
  const [newBundles, setNewBundles] = useState([]);
  const [isValidatingNew, setIsValidatingNew] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropdown sources for per-row sort_no / color overrides.
  const [vendorSortNos, setVendorSortNos] = useState([]);
  const [colors, setColors] = useState([]);

  const staffInputRef = useRef(null);
  const originalBundleRef = useRef(null);
  const newBundleRef = useRef(null);

  useEffect(() => {
    staffInputRef.current?.focus();
    // Master colour list is needed once the original bundle is locked, but
    // fetching it eagerly avoids a perceptible delay when the first new
    // bundle gets scanned.
    (async () => {
      try {
        const r = await apiClient.get('/sections/greige/colors');
        setColors(r.data.colors || []);
      } catch (e) {
        console.error('Failed to load colors', e);
      }
    })();
  }, []);

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
      setTimeout(() => originalBundleRef.current?.focus(), 100);
    }
  };

  const handleOriginalBundleSubmit = async (e) => {
    e.preventDefault();
    if (!originalBundle.trim()) {
      toast.error('Please scan original bundle');
      return;
    }
    
    setIsValidatingOriginal(true);
    try {
      // Validate that the bundle is from Sort No Assignment
      const response = await apiClient.post('/sections/greige/bundle-split/validate-original', null, {
        params: { bundle_barcode: originalBundle.trim() }
      });
      
      setOriginalBundleData(response.data);
      setOriginalBundleLocked(true);
      toast.success(`Bundle validated: ${response.data.sort_no} - ${response.data.color_name}`);

      // Fetch the original vendor's sort_nos so the per-row override
      // dropdown is scoped (Q2=a — original vendor's sort_nos only).
      const vendor = response.data.vendor_name;
      if (vendor) {
        try {
          const sr = await apiClient.get('/sections/greige/sort-nos', {
            params: { vendor_name: vendor },
          });
          // 2026-05-20 — Backend returns full sort_no documents
          // ({sort_no, section_name, vendor_name, fg_name, ...}); the
          // Select dropdown below renders each option as `{sn}` and
          // passes it to SelectItem `value`/`key`. Passing an object
          // there crashes React with "Objects are not valid as a React
          // child" the moment the operator adds the first new bundle.
          // Normalise to a unique string[] of sort_no values up front.
          const raw = sr.data.sort_nos || [];
          const unique = Array.from(
            new Set(
              raw
                .map((s) => (typeof s === 'string' ? s : s?.sort_no))
                .filter(Boolean)
            )
          );
          setVendorSortNos(unique);
        } catch (err) {
          console.error('Failed to fetch sort_nos for vendor', vendor, err);
          setVendorSortNos([]);
        }
      }

      setTimeout(() => newBundleRef.current?.focus(), 100);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to validate bundle';
      toast.error(errorMsg);
      setOriginalBundle('');
      setTimeout(() => originalBundleRef.current?.focus(), 100);
    } finally {
      setIsValidatingOriginal(false);
    }
  };

  const handleNewBundleScan = async (e) => {
    e.preventDefault();
    
    const code = newBundleCode.trim();
    if (!code) {
      toast.error('Please scan new bundle barcode');
      setNewBundleCode('');
      newBundleRef.current?.focus();
      return;
    }

    if (code === originalBundle) {
      toast.error('New bundle cannot be same as original');
      setNewBundleCode('');
      newBundleRef.current?.focus();
      return;
    }

    if (newBundles.some((b) => b.bundle_barcode === code)) {
      toast.error('Bundle already scanned');
      setNewBundleCode('');
      newBundleRef.current?.focus();
      return;
    }

    setIsValidatingNew(true);
    try {
      // Validate that the new bundle code is valid but not already used
      await apiClient.post('/sections/greige/bundle-split/validate-new-bundle', null, {
        params: { bundle_barcode: code }
      });

      // New row defaults to the original bundle's sort_no/color; operator
      // can change inline before submit.
      setNewBundles((prev) => [
        {
          bundle_barcode: code,
          sort_no: originalBundleData?.sort_no || '',
          color_code: originalBundleData?.color_code || '',
          color_name: originalBundleData?.color_name || '',
        },
        ...prev,
      ]);
      toast.success(`New bundle added! Total: ${newBundles.length + 1}`);
      setNewBundleCode('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid bundle code';
      toast.error(errorMsg);
      setNewBundleCode('');
    } finally {
      setIsValidatingNew(false);
      setTimeout(() => newBundleRef.current?.focus(), 50);
    }
  };

  const handleRemoveBundle = (code) => {
    setNewBundles((prev) => prev.filter((b) => b.bundle_barcode !== code));
    toast.success('Bundle removed');
  };

  const updateBundleSortNo = (code, sortNo) => {
    setNewBundles((prev) =>
      prev.map((b) => (b.bundle_barcode === code ? { ...b, sort_no: sortNo } : b))
    );
  };

  const updateBundleColor = (code, colorCode) => {
    const colorObj = colors.find((c) => c.color_code === colorCode);
    setNewBundles((prev) =>
      prev.map((b) =>
        b.bundle_barcode === code
          ? {
              ...b,
              color_code: colorCode,
              color_name: colorObj?.color_name || b.color_name,
            }
          : b
      )
    );
  };

  const handleSubmit = async () => {
    if (newBundles.length < 2) {
      toast.error('Please scan at least 2 new bundle numbers for split');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiClient.post('/sections/greige/bundle-split/submit', {
        staff_id: staffId,
        original_bundle: originalBundle,
        new_bundles: newBundles.map((b) => ({
          bundle_barcode: b.bundle_barcode,
          sort_no: b.sort_no || null,
          color_code: b.color_code || null,
          color_name: b.color_name || null,
        })),
      });

      toast.success(response.data.message);
      
      // Reset for next entry
      setNewBundles([]);
      setOriginalBundle('');
      setOriginalBundleData(null);
      setOriginalBundleLocked(false);
      setVendorSortNos([]);
      setTimeout(() => originalBundleRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setStaffIdLocked(false);
    setStaffId('');
    setOriginalBundle('');
    setOriginalBundleData(null);
    setOriginalBundleLocked(false);
    setNewBundles([]);
    setVendorSortNos([]);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Operation 3: Multiple Bundle Split</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Section - Split Bundle into Multiple</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">How Bundle Split Works:</p>
            <ul className="mt-1 space-y-1 text-blue-700">
              <li>• <strong>Original Bundle:</strong> Must be from Sort No Assignment (Operation 2)</li>
              <li>• <strong>New Bundles:</strong> Must be valid system codes (from Print Codes or uploaded USN) that are NOT already used</li>
              <li>• After split, original bundle becomes <strong>invalid</strong> and new bundles inherit its properties</li>
            </ul>
          </div>
        </div>

        {/* Step 1: Staff ID */}
        <Card className="border-2 border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-purple-600">1</div>
              <User className="w-5 h-5 text-purple-600" />
              Staff Identification
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput
                inputRef={staffInputRef}
                onValidated={handleStaffValidated}
                autoFocus={true}
                label=""
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-600 text-white px-4 py-2 rounded-lg font-mono text-lg">{staffId}</div>
                  {staffName && <span className="text-[#5A5F6E]">{staffName}</span>}
                </div>
                <Button variant="outline" onClick={handleReset}>Change</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Original Bundle */}
        {staffIdLocked && (
          <Card className={`border-2 ${originalBundleLocked ? 'border-green-500 bg-green-50/30' : 'border-purple-600'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-purple-600">2</div>
                <Package className="w-5 h-5 text-purple-600" />
                Original Bundle (from Sort No Assignment)
              </CardTitle>
              <CardDescription>Scan the bundle that needs to be split</CardDescription>
            </CardHeader>
            <CardContent>
              {!originalBundleLocked ? (
                <form onSubmit={handleOriginalBundleSubmit} className="flex gap-3">
                  <Input 
                    ref={originalBundleRef} 
                    value={originalBundle} 
                    onChange={(e) => setOriginalBundle(e.target.value)} 
                    placeholder="Scan Original Bundle QR code..." 
                    className="text-lg h-12 font-mono" 
                    autoComplete="off"
                    disabled={isValidatingOriginal}
                  />
                  <Button 
                    type="submit" 
                    className="h-12 bg-purple-600 hover:bg-purple-700"
                    disabled={isValidatingOriginal}
                  >
                    {isValidatingOriginal ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Validate
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="bg-purple-600 text-white px-4 py-2 rounded-lg font-mono text-lg">{originalBundle}</div>
                    <Button variant="outline" onClick={() => { setOriginalBundleLocked(false); setOriginalBundle(''); setOriginalBundleData(null); setNewBundles([]); }}>
                      Change
                    </Button>
                  </div>
                  
                  {/* Show original bundle properties */}
                  {originalBundleData && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-amber-800 mb-2">Properties to be inherited by new bundles:</p>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-amber-600">Vendor:</span>
                          <p className="font-semibold text-amber-900">{originalBundleData.vendor_name || '-'}</p>
                        </div>
                        <div>
                          <span className="text-amber-600">Sort No:</span>
                          <p className="font-semibold text-amber-900">{originalBundleData.sort_no || '-'}</p>
                        </div>
                        <div>
                          <span className="text-amber-600">Color:</span>
                          <p className="font-semibold text-amber-900">{originalBundleData.color_name || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: New Bundles */}
        {originalBundleLocked && (
          <Card className="border-2 border-purple-600">
            <CardHeader className="pb-3 bg-purple-50">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-purple-600">3</div>
                <Split className="w-5 h-5 text-purple-600" />
                Scan New Bundles
              </CardTitle>
              <CardDescription>Scan valid system codes (from Print Codes or uploaded USN) that are NOT already used</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleNewBundleScan} className="flex gap-3">
                <Input 
                  ref={newBundleRef} 
                  value={newBundleCode} 
                  onChange={(e) => setNewBundleCode(e.target.value)} 
                  placeholder="Scan New Bundle QR code..." 
                  className="text-lg h-14 font-mono" 
                  disabled={isValidatingNew} 
                  autoComplete="off" 
                />
                <Button 
                  type="submit" 
                  className="h-14 px-8 bg-purple-600 hover:bg-purple-700" 
                  disabled={isValidatingNew}
                >
                  {isValidatingNew ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Package className="w-5 h-5 mr-2" />
                      Add
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* New Bundles List */}
        {originalBundleLocked && newBundles.length > 0 && (
          <Card className="border-2 border-[#E5DFD0]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">New Bundles ({newBundles.length})</CardTitle>
                  <CardDescription>
                    Will replace <span className="font-mono font-semibold">{originalBundle}</span> 
                    {originalBundleData && (
                      <span className="text-amber-600"> ({originalBundleData.sort_no}, {originalBundleData.color_name})</span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Button 
                    onClick={handleSubmit} 
                    disabled={isSubmitting || newBundles.length < 2} 
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {isSubmitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit Split
                  </Button>
                  {newBundles.length < 2 && (
                    <p className="text-xs text-amber-600">Minimum 2 bundles required</p>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[28rem] overflow-y-auto">
                {newBundles.map((nb, index) => {
                  const isOverridden =
                    (nb.sort_no && nb.sort_no !== originalBundleData?.sort_no) ||
                    (nb.color_code && nb.color_code !== originalBundleData?.color_code);
                  return (
                    <div
                      key={nb.bundle_barcode}
                      className={`p-3 rounded-lg border ${
                        isOverridden
                          ? 'bg-purple-50 border-purple-300'
                          : 'bg-[#F5F1E8] border-transparent'
                      }`}
                      data-testid={`new-bundle-row-${nb.bundle_barcode}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-[#5A5F6E] w-8">
                            #{newBundles.length - index}
                          </span>
                          <p className="font-mono font-semibold text-[#1A1F2E]">
                            {nb.bundle_barcode}
                          </p>
                          {isOverridden ? (
                            <Badge className="bg-purple-200 text-purple-800 text-[10px]">
                              Overridden
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 text-[10px]">
                              Inherits original
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveBundle(nb.bundle_barcode)}
                          className="text-red-500 hover:bg-red-50"
                          data-testid={`remove-bundle-${nb.bundle_barcode}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pl-11">
                        <div>
                          <label className="text-[11px] uppercase tracking-wide text-stone-500 font-medium">
                            Sort No
                          </label>
                          <Select
                            value={nb.sort_no || ''}
                            onValueChange={(v) =>
                              updateBundleSortNo(nb.bundle_barcode, v)
                            }
                          >
                            <SelectTrigger
                              className="h-9 text-sm bg-white"
                              data-testid={`sort-no-trigger-${nb.bundle_barcode}`}
                            >
                              <SelectValue placeholder="Select sort no..." />
                            </SelectTrigger>
                            <SelectContent>
                              {vendorSortNos.length === 0 ? (
                                <SelectItem value="__none__" disabled>
                                  No sort_nos for this vendor
                                </SelectItem>
                              ) : (
                                vendorSortNos.map((sn) => (
                                  <SelectItem
                                    key={sn}
                                    value={sn}
                                    data-testid={`sort-no-option-${sn}`}
                                  >
                                    {sn}
                                    {sn === originalBundleData?.sort_no && (
                                      <span className="ml-1 text-[10px] text-stone-400">
                                        (original)
                                      </span>
                                    )}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-[11px] uppercase tracking-wide text-stone-500 font-medium">
                            Color
                          </label>
                          <Select
                            value={nb.color_code || ''}
                            onValueChange={(v) =>
                              updateBundleColor(nb.bundle_barcode, v)
                            }
                          >
                            <SelectTrigger
                              className="h-9 text-sm bg-white"
                              data-testid={`color-trigger-${nb.bundle_barcode}`}
                            >
                              <SelectValue placeholder="Select color...">
                                {nb.color_name || nb.color_code || 'Select color…'}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {colors.map((c) => (
                                <SelectItem
                                  key={c.color_code}
                                  value={c.color_code}
                                  data-testid={`color-option-${c.color_code}`}
                                >
                                  {c.color_name}
                                  <span className="ml-1 text-[10px] text-stone-400">
                                    ({c.color_code})
                                  </span>
                                  {c.color_code === originalBundleData?.color_code && (
                                    <span className="ml-1 text-[10px] text-stone-400">
                                      · original
                                    </span>
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Warning about original bundle */}
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  <p className="font-medium">After submission:</p>
                  <p>Original bundle <span className="font-mono font-semibold">{originalBundle}</span> will become <strong>INVALID</strong> and cannot be used in further operations.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default BundleSplit;
